/**
 * Media data (server module `media`): the video library, requests, footage availability,
 * camera constraints, the monthly quota and the auto-video flow templates. Other modules may
 * use these hooks (e.g. Activity plays and requests video for an event).
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type { components } from '@/lib/api/schema';

type S = components['schemas'];
export type Video = S['VideoOut'];
export type VideoRequest = S['RequestOut'];
export type Footage = S['FootageOut'];
export type Constraints = S['ConstraintsOut'];
export type Quota = S['QuotaOut'];
export type FlowTemplate = S['FlowTemplate'];
export type RequestBody = S['RequestBody'];

export const mediaKeys = {
  all: ['media'] as const,
  videos: (p: object) => ['media', 'videos', p] as const,
  video: (id: string) => ['media', 'video', id] as const,
  requests: (p: object) => ['media', 'requests', p] as const,
  request: (id: string) => ['media', 'request', id] as const,
  footage: (p: object) => ['media', 'footage', p] as const,
  constraints: (vehicle: string) => ['media', 'constraints', vehicle] as const,
  quota: (project: string) => ['media', 'quota', project] as const,
  templates: ['media', 'flow-templates'] as const,
};

export interface VideosQuery {
  project?: string[];
  vehicle?: string;
  driver?: string;
  channel?: string;
  event_type?: string[];
  detection?: string;
  start?: string;
  end?: string;
  include_deleted?: boolean;
}

export function useVideos(p: VideosQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: mediaKeys.videos(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/media/videos', { params: { query: { ...p, cursor: pageParam, limit: 60 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useVideo(id: string | undefined) {
  return useQuery({
    queryKey: mediaKeys.video(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/media/videos/{video_id}', { params: { path: { video_id: id ?? '' } } })),
    enabled: !!id,
  });
}

export interface RequestsQuery {
  project?: string[];
  vehicle?: string;
  driver?: string;
  status?: string[];
  active?: boolean;
  origin?: 'user' | 'automation';
  start?: string;
  end?: string;
}

export function useRequests(p: RequestsQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: mediaKeys.requests(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/media/requests', { params: { query: { ...p, cursor: pageParam, limit: 100 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    // A request in flight changes state on its own; refresh while one is open.
    refetchInterval: (q) => (q.state.data?.pages.some((pg) => pg.items.some((r) => ACTIVE.has(r.status))) ? 20_000 : false),
    enabled,
  });
}

export function useRequest(id: string | undefined) {
  return useQuery({
    queryKey: mediaKeys.request(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/media/requests/{request_id}', { params: { path: { request_id: id ?? '' } } })),
    refetchInterval: (q) => (q.state.data && ACTIVE.has(q.state.data.status) ? 15_000 : false),
    enabled: !!id,
  });
}

/** The request timeline of one vehicle: footage on the camera, stored clips, requests in flight. */
export function useFootage(vehicle: string | undefined, w: { start: string; end: string }, enabled = true) {
  return useQuery({
    queryKey: mediaKeys.footage({ vehicle, ...w }),
    queryFn: () => unwrap(api.GET('/api/media/footage', { params: { query: { vehicle: vehicle ?? '', ...w } } })),
    placeholderData: keepPreviousData,
    enabled: !!vehicle && enabled,
  });
}

/** What can be requested for a vehicle (camera, channels, longest clip, reachability, quota). */
export function useConstraints(vehicle: string | undefined, enabled = true) {
  return useQuery({
    queryKey: mediaKeys.constraints(vehicle ?? ''),
    queryFn: () => unwrap(api.GET('/api/media/constraints', { params: { query: { vehicle: vehicle ?? '' } } })),
    enabled: !!vehicle && enabled,
    staleTime: 30_000,
  });
}

/** The monthly quota of each project. */
export function useQuotas(projects: string[], enabled = true) {
  return useQueries({
    queries: projects.map((project) => ({
      queryKey: mediaKeys.quota(project),
      queryFn: () => unwrap(api.GET('/api/media/quota', { params: { query: { project } } })),
      staleTime: 60_000,
      enabled,
    })),
  });
}

export function useFlowTemplates(enabled = true) {
  return useQuery({
    queryKey: mediaKeys.templates,
    queryFn: () => unwrap(api.GET('/api/media/flow-templates')),
    staleTime: 10 * 60_000,
    enabled,
  });
}

export const ACTIVE = new Set(['created', 'queued', 'sent', 'uploading']);

/** Request video (action `media.request_video`): one request per channel, or the one that exists. */
export function useRequestVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<RequestBody>) =>
      unwrap(api.POST('/api/media/requests', { body: { only_available_channels: false, note: '', ...body } as RequestBody })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mediaKeys.all });
      void qc.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useCancelRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; reason?: string }) =>
      unwrap(api.POST('/api/media/requests/{request_id}/cancel', { params: { path: { request_id: v.id } }, body: { reason: v.reason ?? '' } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mediaKeys.all });
      void qc.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useDeleteVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; reason: string }) =>
      unwrap(api.POST('/api/media/videos/{video_id}/delete', { params: { path: { video_id: v.id } }, body: { reason: v.reason } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mediaKeys.all });
      void qc.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useRefreshFootage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vehicleId: string) => unwrap(api.POST('/api/media/footage/refresh', { body: { vehicle_id: vehicleId } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', 'footage'] }),
  });
}
